import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/utils/formatters';
import type {
  ParsedLPWaterfallResult,
  ParsedDistributionWaterfallResult,
} from '@/hooks/react-query/useDistributionWaterfall';
import type { WaterfallMetric } from './WaterfallContent';
import { WaterfallLPRow } from './WaterfallLPRow';

interface WaterfallTableProps {
  investors: ParsedLPWaterfallResult[];
  currency: string;
  selectedMetric: WaterfallMetric;
  isTheoretical: boolean;
  searchTerm: string;
  overall: ParsedDistributionWaterfallResult;
  totalLp: number;
  totalGp: number;
  totalProfit: number;
}

interface WaterfallHeaderRowProps {
  selectedMetric: WaterfallMetric;
  isTheoretical: boolean;
}

function WaterfallHeaderRow({
  selectedMetric,
  isTheoretical,
}: WaterfallHeaderRowProps) {
  const lpTotalLabel = isTheoretical
    ? 'Cumulative Theo. LP Share'
    : 'Total LP Distributions';
  const gpTotalLabel = isTheoretical
    ? 'Cumulative Theo. Carry'
    : 'Total Carried Interest';

  return (
    <TableRow className="hover:bg-transparent">
      <TableHead className="text-xs font-bold uppercase tracking-wider">
        Investor
      </TableHead>
      {(!selectedMetric || selectedMetric === 'called_capital') && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          {selectedMetric ? 'Total Contributed' : 'Called'}
        </TableHead>
      )}
      {selectedMetric === 'total_distribution_amount' && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          {isTheoretical ? 'Total Value (NAV + Dist.)' : 'Total Distributions'}
        </TableHead>
      )}
      {selectedMetric === 'preferred_return_amount' && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          Accrued Hurdle
        </TableHead>
      )}
      {(!selectedMetric || selectedMetric === 'total_lp') && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          {selectedMetric ? lpTotalLabel : 'LP Total'}
        </TableHead>
      )}
      {(!selectedMetric || selectedMetric === 'total_gp') && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          {selectedMetric ? gpTotalLabel : 'GP Total'}
        </TableHead>
      )}
      {selectedMetric === 'total_profit' && (
        <TableHead className="text-right text-xs font-bold uppercase tracking-wider">
          {isTheoretical ? 'Cumulative Theo. Profit' : 'Net Realized Profit'}
        </TableHead>
      )}
    </TableRow>
  );
}

export function WaterfallTable({
  investors,
  currency,
  selectedMetric,
  isTheoretical,
  searchTerm,
  overall,
  totalLp,
  totalGp,
  totalProfit,
}: WaterfallTableProps) {
  return (
    <div className="rounded-xl border overflow-hidden bg-card shadow-sm">
      <Table>
        <TableHeader className="bg-muted/30">
          <WaterfallHeaderRow
            selectedMetric={selectedMetric}
            isTheoretical={isTheoretical}
          />
        </TableHeader>
        <TableBody>
          {investors.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                className="text-center py-8 text-muted-foreground"
              >
                {searchTerm
                  ? `No investors found matching "${searchTerm}"`
                  : 'No investors found.'}
              </TableCell>
            </TableRow>
          ) : (
            investors.map((lp) => (
              <WaterfallLPRow
                key={lp.investor_id}
                lp={lp}
                currency={currency}
                selectedMetric={selectedMetric}
              />
            ))
          )}
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell className="font-bold text-sm">Total</TableCell>
            {(!selectedMetric || selectedMetric === 'called_capital') && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(overall.called_capital, { currency })}
              </TableCell>
            )}
            {selectedMetric === 'total_distribution_amount' && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(overall.total_distribution_amount, {
                  currency,
                })}
              </TableCell>
            )}
            {selectedMetric === 'preferred_return_amount' && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(overall.preferred_return_amount, { currency })}
              </TableCell>
            )}
            {(!selectedMetric || selectedMetric === 'total_lp') && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(totalLp, { currency })}
              </TableCell>
            )}
            {(!selectedMetric || selectedMetric === 'total_gp') && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(totalGp, { currency })}
              </TableCell>
            )}
            {selectedMetric === 'total_profit' && (
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(totalProfit, { currency })}
              </TableCell>
            )}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
